import React, { useEffect, useRef, useState } from "react";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { useAuth } from "contexts/AuthProvider";
import { axiosInstance } from "contexts/axiosApiInstance";
import { UserAccountResponse } from "components/entities";
import {
  AvatarInitial,
  invalidatePhotoCache,
} from "components/common/AvatarInitial";
import { TooltipButton } from "components/common/TooltipButton";
import { EditableName } from "components/canvas/utils/EditableName";
import { DropdownMenu } from "components/common/DropdownMenu";
import { MenuItem } from "components/common/MenuItem";
import { createLogger } from "debug/DebugingLogger";
import { showNotificationGlobal } from "stores/useNotificationStore";
import {
  IMAGE_ACCEPTED_TYPES,
  IMAGE_ACCEPT_STRING,
  IMAGE_MAX_FILE_SIZE,
  resizeImage,
} from "@/lib/images";

const log = createLogger("SettingsDialog");

interface GeneralTabProps {
  accountDetails: UserAccountResponse | undefined;
  email: string;
  isScheduledForDeletion: boolean;
  onDeleteAccount: () => void;
  onClose: () => void;
  onAccountUpdated: () => void;
}

export const GeneralTab: React.FC<GeneralTabProps> = ({
  accountDetails,
  email,
  isScheduledForDeletion,
  onDeleteAccount,
  onClose,
  onAccountUpdated,
}) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showSavedTick, setShowSavedTick] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const savedTickTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editBtnRef = useRef<HTMLDivElement>(null);
  const { setAccountStatus } = useAuth();

  const savedName = accountDetails?.account_details?.name || "";
  const currentPhoto = accountDetails?.account_details?.photo || "";

  useEffect(() => {
    return () => {
      if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
    };
  }, []);

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (
      !IMAGE_ACCEPTED_TYPES.includes(
        file.type as (typeof IMAGE_ACCEPTED_TYPES)[number],
      )
    ) {
      showNotificationGlobal(
        "Please upload a JPEG, PNG, WebP, or SVG image",
        "error",
      );
      return;
    }

    if (file.size > IMAGE_MAX_FILE_SIZE) {
      showNotificationGlobal("Image must be under 1 MB", "error");
      return;
    }

    setIsUploadingPhoto(true);
    try {
      const dataUrl = await resizeImage(file);
      await axiosInstance.put("/action/user/photo", { photo: dataUrl });
      invalidatePhotoCache();
      onAccountUpdated();
    } catch (error) {
      log.error("Failed to upload photo: %o", error);
      const message =
        error instanceof Error && error.message === "Failed to load image"
          ? "Could not process image — the file may be corrupted"
          : "Failed to upload photo";
      showNotificationGlobal(message, "error");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = async () => {
    setIsUploadingPhoto(true);
    try {
      await axiosInstance.delete("/action/user/photo");
      invalidatePhotoCache();
      onAccountUpdated();
    } catch (error) {
      log.error("Failed to remove photo: %o", error);
      showNotificationGlobal("Failed to remove photo", "error");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // `EditableName.commit` is called synchronously and inspects the return value,
  // so this must stay sync — fire the PUT off and handle its result later.
  const commitName = (name: string): void => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === savedName) return;
    void axiosInstance
      .put("/action/user/display-name", { name: trimmed })
      .then(() => {
        setShowSavedTick(true);
        if (savedTickTimer.current) clearTimeout(savedTickTimer.current);
        savedTickTimer.current = setTimeout(
          () => setShowSavedTick(false),
          2000,
        );
        onAccountUpdated();
      })
      .catch((error: unknown) => {
        log.error("Failed to update display name: %o", error);
        showNotificationGlobal("Failed to update display name", "error");
      });
  };

  const handleCancelDeletion = async () => {
    setIsCancelling(true);
    try {
      await axiosInstance.post("/action/cancel_account_deletion");
      setAccountStatus("active");
      showNotificationGlobal("Account deletion cancelled", "success");
      onClose();
    } catch (error) {
      log.error("Failed to cancel account deletion: %o", error);
      showNotificationGlobal("Failed to cancel account deletion", "error");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 items-center">
        <div className="shrink-0 relative group" ref={editBtnRef}>
          <AvatarInitial
            name={savedName || ""}
            icon={currentPhoto}
            color={accountDetails?.account_details?.color || ""}
            clientId={accountDetails?.account_details?.id}
            size="lg"
          />
          <div
            className="absolute inset-0 rounded-full flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity bg-black/40"
            style={isUploadingPhoto ? { opacity: 1 } : undefined}
            onClick={() => !isUploadingPhoto && setShowPhotoMenu((v) => !v)}
          >
            {isUploadingPhoto ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <CameraAltOutlinedIcon sx={{ fontSize: 20, color: "white" }} />
            )}
          </div>
          <DropdownMenu
            anchorRef={editBtnRef}
            isOpen={showPhotoMenu}
            onClose={() => setShowPhotoMenu(false)}
            style={{ width: "auto", minWidth: "9rem" }}
          >
            <MenuItem
              Icon={CameraAltOutlinedIcon}
              label="Upload photo"
              onClick={() => {
                setShowPhotoMenu(false);
                fileInputRef.current?.click();
              }}
            />
            {currentPhoto && (
              <MenuItem
                Icon={DeleteOutlineOutlinedIcon}
                label="Remove photo"
                onClick={() => {
                  setShowPhotoMenu(false);
                  handleRemovePhoto();
                }}
              />
            )}
          </DropdownMenu>
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT_STRING}
            className="hidden"
            onChange={handleUploadPhoto}
          />
        </div>
        <div className="flex flex-col flex-1 min-w-0">
          <label className="text-fg-medium flex item-height items-center">
            Display Name
          </label>
          <div className="group flex items-center item-height text-fg-muted-regular cursor-text gap-1">
            <EditableName
              name={savedName || "Not set"}
              isEditing={isEditingName}
              canEdit={true}
              setIsEditing={setIsEditingName}
              update={() => {}}
              commit={commitName}
              abort={() => setIsEditingName(false)}
              editColor="var(--color-accent)"
            />
            {!isEditingName && showSavedTick && (
              <CheckIcon
                sx={{ fontSize: 14, color: "var(--color-success)" }}
                className="ml-1"
              />
            )}
            {!isEditingName && !showSavedTick && (
              <span className="invisible group-hover:visible">
                <TooltipButton
                  title="Edit"
                  variant="default"
                  size="xs"
                  onClick={() => setIsEditingName(true)}
                >
                  <EditOutlinedIcon />
                </TooltipButton>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-0.5">
        <label className="text-fg-medium flex">Email</label>
        <span className="text-fg-muted-regular flex items-center item-height">
          {email}
        </span>
      </div>

      {isScheduledForDeletion ? (
        <div className="flex flex-col bg-error/5 px-4 py-2 rounded-app">
          <div className="flex items-center gap-2 item-height">
            <WarningAmberOutlinedIcon
              sx={{ fontSize: 14, color: "var(--color-error)" }}
            />
            <span className="text-error flex-1">
              Account scheduled for deletion
            </span>
            <TooltipButton
              title="Cancel Deletion"
              variant="contextual"
              size="xs"
              onClick={handleCancelDeletion}
              disabled={isCancelling}
            >
              <RestoreOutlinedIcon />
            </TooltipButton>
          </div>
          <p className="text-fg-muted-regular flex items-center item-height">
            Your organization, its spaces and models are in the Trash until then
            — cancelling brings everything back.
          </p>
        </div>
      ) : (
        <button
          onClick={onDeleteAccount}
          className="text-fg-muted-medium flex items-center item-height cursor-pointer hover:text-error"
        >
          Delete Account
        </button>
      )}
    </div>
  );
};
